import { ActionModal } from '@kcftech/design-system';
import { IAssemblyApplicationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { AssemblyParameterFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterFormContent';
import { getAssemblyFieldElementRequest } from 'pages/Settings/AssemblyType/parameter_form/shared';
import {
    AssemblyParameterElementDto,
    AssemblyParameterFormData,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { useAssemblyParameterForm } from 'pages/Settings/AssemblyType/parameter_form/useAssemblyParameterForm';
import { createOrUpdateAssemblyParameter } from 'utils/assembly_application';

export type EditAssemblyParameterProps = Readonly<{
    application: IAssemblyApplicationResponse;
    parameter: AssemblyParameterElementDto;
    onClose: (saved: boolean) => void;
}>;

export function EditAssemblyParameterModal({
    application,
    parameter,
    onClose,
}: EditAssemblyParameterProps) {
    const siblingParameters = application.ParametersDefinition?.Data
        ? Object.values(application.ParametersDefinition.Data).filter(
              (param) => param.FieldKey !== parameter.FieldKey,
          )
        : [];

    const { showToast } = useNotificationContext();

    const form = useAssemblyParameterForm(parameter);

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const { mutate: createParameter, isPending: isSaving } = useMutation({
        mutationFn: (formData: AssemblyParameterFormData) =>
            createOrUpdateAssemblyParameter(
                application.Id,
                getAssemblyFieldElementRequest(formData),
            ),

        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully edited assembly application parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to edit assembly application parameter',
            }),
    });

    const onSave = handleSubmit((formData) => createParameter(formData));

    return (
        <ActionModal
            aria-label="Edit Assembly Application Parameter"
            title="Edit Assembly Application Parameter"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isSaving)
            }
            onAction={onSave}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
        >
            <AssemblyParameterFormContent
                isEdit
                form={form}
                siblingParameters={siblingParameters}
            />
        </ActionModal>
    );
}
